<template>
  <div class="form-input" ref="containerRef">
    <label class="label-input">{{ label }}</label>

    <div
      class="custom-select"
      :class="{ disabled: isDisabled }"
      @click="toggleDropdown"
    >
      <div class="selected-option">
        {{
          selectedLabel ||
          (isDisabled ? "Deshabilitado" : "Selecciona el rol...")
        }}
      </div>
      <img
        class="select-arrow"
        src="@/assets/icons/DownArrow.svg"
        alt="arrow"
        :class="{ rotated: isOpen }"
      />
      <ul v-show="isOpen && options?.length" class="select-options">
        <li
          v-for="(option, index) in options"
          :key="index"
          class="option-item"
          @click.stop="selectOption(option)"
        >
          {{ choiceMessage(option.enValue, option.esValue) }}
        </li>
      </ul>
    </div>

    <p :class="[hasError ? 'error' : 'no-error']">
      {{ hasError ? error?.esMessage : "" }}
    </p>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from "vue";
import { MessageError } from "@/business/shared/commons/validator/validatorsTypes";
import { SelectOption } from "@/business/shared/commons/selectOption";

export default defineComponent({
  name: "GeneralSelect",
  props: {
    label: {
      type: String,
      default: "",
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array as () => SelectOption[],
      required: true,
    },
    value: {
      type: String,
      default: "",
    },
    error: {
      type: Object as () => MessageError,
      default: null,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:text", "changeOption"],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const optionSelected = ref(props.value || "");
    const containerRef = ref<HTMLElement | null>(null);

    const selectedLabel = computed(() => {
      if (!optionSelected.value || !props.options?.length) return "";
      const selected = props.options.find(
        (opt) => opt.id === optionSelected.value
      );
      return selected ? selected.esValue : "";
    });

    const toggleDropdown = () => {
      if (!props.isDisabled && props.options?.length) {
        isOpen.value = !isOpen.value;
      }
    };

    const closeDropdown = () => {
      isOpen.value = false;
    };

    const selectOption = (option: SelectOption) => {
      if (!option) return;
      optionSelected.value = option.id;
      emit("update:text", option.id);
      emit("changeOption", option.id);
      closeDropdown();
    };

    watch(
      () => props.value,
      (newVal) => {
        optionSelected.value = newVal || "";
      },
      { immediate: true }
    );

    watch(
      () => props.options,
      (newOptions) => {
        if (!newOptions?.length) {
          closeDropdown();
        }
      }
    );

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.value &&
        !containerRef.value.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    function choiceMessage(enValue: string, esValue: string) {
      return esValue || enValue;
    }

    return {
      isOpen,
      optionSelected,
      toggleDropdown,
      closeDropdown,
      selectOption,
      selectedLabel,
      containerRef,
      choiceMessage,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/styles/commons/controls/generalSelectStyle";
</style>
